/**
 * Onglet « Synthèse ».
 *
 * Les évaluations viennent du moteur : elles se calculent à partir de seuils
 * déclarés en configuration versionnée, jamais écrits dans ce gabarit.
 */

import {
  entier,
  euros,
  eurosSigne,
  libelleEvaluation,
  pourcent,
  type LigneSynthese,
} from '@scc/core';
import type { Charge } from '../api.js';
import { h } from '../dom.js';
import { badge, barreExport, enteteSection, noteLecture, regleAccent } from '../composants.js';

/** Situation et tendance rendues en clair, à partir des données du tableau. */
function libelles(l: LigneSynthese, d: Charge): { situation: string; tendance: string } {
  const a = d.adherents;
  const derniere = a.seriesClotures[a.seriesClotures.length - 1];
  const t = (cle: string) => d.trajectoires.find((x) => x.cle === cle);
  const fin = (cle: string) => {
    const x = t(cle);
    return x?.annees[x.annees.length - 1];
  };

  switch (l.cle) {
    case 'cloture':
      return {
        situation: `Résultat ${eurosSigne(d.cloture.resultat)}, fonds propres ${euros(d.chainage.fondsPropresCloture)}`,
        tendance: `${eurosSigne(d.chainage.ecartAuPrevisionnel)} vs prévisionnel`,
      };
    case 'execution':
      return {
        situation: `${euros(d.execution.encaissements)} encaissés, ${euros(d.execution.decaissementsRattaches)} décaissés`,
        tendance: `${pourcent(d.tauxExecution.produits, 0)} des produits contre ${pourcent(d.tauxExecution.charges, 0)} des charges`,
      };
    case 'appel_cotisations': {
      const c = d.facturation.lignes.find((f) => f.exercice === d.meta.exerciceCourant);
      const p = d.facturation.lignes.find((f) => f.exercice === d.meta.exerciceCourant - 1);
      return {
        situation: `${entier(c?.nbFactures ?? 0)} factures contre ${entier(p?.nbFactures ?? 0)} un an plus tôt`,
        tendance: `${pourcent(d.facturation.ratio, 0)} du rythme précédent`,
      };
    }
    case 'ressources_conventionnelles':
      return {
        situation:
          `${euros(d.projection.resteARecevoirConventionnel)} restant à recevoir sur ${euros(d.projection.conventionnelProjete)}` +
          (d.projection.versementsNotifies.length > 0 ? ` attendus (${euros(d.previsionnelVote.totalConventionnel)} votés)` : ''),
        tendance: `${pourcent(d.tauxExecution.conventionnel, 0)} encaissés`,
      };
    case 'resultat_projete':
      return {
        situation: `${eurosSigne(d.projection.resultatProjete)} contre ${eurosSigne(d.projection.resultatVote)} prévus`,
        tendance: eurosSigne(d.projection.ecartResultat),
      };
    case 'reserve':
      return {
        situation: `${euros(d.chainage.reserveAttendue)} attendus`,
        tendance: `${d.chainage.multipleSeuil.toFixed(1).replace('.', ',')} fois le seuil de sécurité`,
      };
    case 'base_comptage':
      return {
        situation: `${entier(derniere?.payeurs ?? 0)} adhérents payeurs en ${derniere?.annee ?? ''} (base Directus)`,
        tendance: 'définition arrêtée',
      };
    default: {
      const cle = l.cle.replace('trajectoire_', '');
      const x = t(cle);
      return {
        situation: `${euros(x?.reserveFinale ?? 0)} de réserve fin ${fin(cle)?.annee ?? ''}`,
        tendance: `${eurosSigne(x?.resultatCumule ?? 0)} cumulés`,
      };
    }
  }
}

export function vueSynthese(d: Charge): HTMLElement {
  const sc3 = d.trajectoires.find((t) => t.cle === 'sc3');
  const derniereSc3 = sc3?.annees[sc3.annees.length - 1];
  const annees = sc3?.annees.map((x) => x.annee) ?? [];

  const table = h(
    'table',
    { classe: 'summary-table encadre-tableau' },
    h('caption', { classe: 'invisible' }, 'Synthèse pour le Conseil national'),
    h(
      'thead',
      {},
      h(
        'tr',
        {},
        h('th', { scope: 'col' }, 'Dimension'),
        h('th', { scope: 'col' }, 'Situation'),
        h('th', { scope: 'col' }, 'Tendance'),
        h('th', { scope: 'col' }, 'Évaluation'),
      ),
    ),
    h(
      'tbody',
      {},
      ...d.synthese.map((l) => {
        const { situation, tendance } = libelles(l, d);
        return h(
          'tr',
          {},
          h('th', { scope: 'row' }, l.dimension),
          h('td', {}, situation),
          h('td', {}, tendance),
          h('td', {}, badge(l.evaluation, libelleEvaluation(l.evaluation))),
        );
      }),
    ),
  );

  return h(
    'div',
    { classe: 'page', id: 'page-synthese', role: 'region', 'aria-label': 'Synthèse' },
    h(
      'section',
      {},
      enteteSection(1, 'Synthèse pour le Conseil national', 'Ce qui est acquis, ce qui reste à décider'),
      table,
      noteLecture(
        `Les évaluations « favorable », « vigilance » et « alerte » se calculent à partir de seuils déclarés dans un ` +
          `fichier de configuration versionné. Elles ne sont pas saisies à la main.`,
      ),
      barreExport('synthese', 'Synthèse'),
      regleAccent('Décisions à instruire'),
      h(
        'p',
        { classe: 'text-block' },
        `**Le calendrier de facturation et d'encaissement.** Rattraper le retard sur l'appel des cotisations, ` +
          `facturer sans délai les ressources conventionnelles encore à zéro et obtenir le règlement des créances ` +
          `ouvertes ramènerait l'exercice près de ` +
          `${d.projection.sourceCotisations === 'estimation' ? 'sa projection de clôture' : 'sa cible budgétaire'}. Il reste ` +
          `${euros(d.projection.resteARecevoir)} à encaisser d'ici décembre, dont ` +
          `${euros(d.projection.resteARecevoirConventionnel)} de ressources conventionnelles et publiques. C'est la ` +
          `mesure la plus immédiate et la plus sûre.`,
      ),
      h(
        'p',
        { classe: 'text-block' },
        `**Le modèle de ressources.** Le scénario 2 repose sur un doublement des effectifs, qu'aucune série ` +
          `historique ne documente — le record constaté est de ${entier(d.adherents.recordHistorique)} adhérents ` +
          `payeurs. Le scénario 1 suppose une érosion du produit moyen que rien n'annonce. Le scénario 3 chiffre la ` +
          `voie intermédiaire : ${entier(derniereSc3?.adherents ?? 0)} adhérents payeurs en ` +
          `${annees[annees.length - 1] ?? ''} et un produit moyen prolongeant la pente observée hors exercices ` +
          `atypiques. Il ` +
          (sc3?.retourEquilibre
            ? `ramène l'exercice à l'équilibre en ${sc3.retourEquilibre} et `
            : 'ne ramène pas l’exercice à l’équilibre sur la période et ') +
          `laisse la réserve à ${euros(sc3?.reserveFinale ?? 0)}, soit ` +
          `${((sc3?.reserveFinale ?? 0) / (derniereSc3?.seuil ?? 1)).toFixed(2).replace('.', ',')} fois le seuil de ` +
          `sécurité, après un creux à ${euros(sc3?.creuxReserve.montant ?? 0)} en ${sc3?.creuxReserve.annee ?? ''}.`,
      ),
      h(
        'p',
        { classe: 'text-block' },
        `**La structure de charges.** Les charges de personnel représentent ` +
          `${pourcent(d.previsionnelVote.charges.personnel / d.previsionnelVote.totalCharges, 0)} du budget voté. ` +
          `À ressources inchangées, tout retour à l'équilibre passe soit par une croissance des adhésions supérieure ` +
          `à celle du scénario prudent, soit par un arbitrage explicite sur cette enveloppe.`,
      ),
      h(
        'div',
        { classe: 'insight info' },
        h('span', { classe: 'insight-icon', 'aria-hidden': 'true' }, 'ℹ'),
        h('p', {}, d.meta.reserveRaccordement.mention),
      ),
    ),
  );
}
